/**
 * The selectable skeleton drawn on top of the mesh: a sphere per joint, a line per bone.
 *
 * Everything here lives in the overlay group with `depthTest: false`, so joints stay clickable
 * even when they are buried inside the mesh — a skeleton you can only select from outside the
 * silhouette is useless for posing.
 *
 * Three sources feed it, chosen by mode (see `overlayData.ts`):
 *  - ANIMATE / MAP → live world positions of the bound rig's mapped bones,
 *  - RIG           → the placement being edited (includes `Root`),
 *  - MAP also      → every *source* bone drawn faintly, so unmapped bones are visible and the
 *                    user can see what they still have to map.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Raycaster,
  SphereGeometry,
  Vector2,
} from 'three';
import type { Camera, Object3D } from 'three';

import { analyzeSkeleton, collectBoneObjects } from '../../rig/sourceSkeleton';
import type { CanonicalBoneId, Vec3Tuple } from '../../shared';
import type { EditorContext } from '../context';
import { overlayForMode } from './overlayData';
import type { SkeletonOverlayData } from './overlayData';

const JOINT_RADIUS = 0.022;
/** Invisible pick sphere: a 2.2 cm dot is far too small to hit reliably with a mouse. */
const PICK_RADIUS = 0.055;

/*
 * Design rule 2, viewport side (`design/ui-design.md` §6, step 9): the viewport's selection colour
 * is ORANGE and nothing else in here competes with it. Unselected bones are neutral grey, so the
 * one thing the user is holding is the only saturated thing on screen.
 */
const COLOR_NORMAL = new Color(0xc9c9c9);
const COLOR_HOVER = new Color(0xffffff);
const COLOR_SELECTED = new Color(0xed8a3c);
const COLOR_LINK = 0x6f6f6f;
const COLOR_SOURCE = 0x5a5a5a;

interface JointVisual {
  id: CanonicalBoneId;
  dot: Mesh;
  pick: Mesh;
  material: MeshBasicMaterial;
}

export interface SkeletonLayer {
  update(): void;
  /** Viewport display switch (`Skeleton` in the overlay bar). Does not touch editor state. */
  setVisible(visible: boolean): void;
  /** Canonical bone under the pointer, in normalized device coords, or null. */
  pick(ndc: Vector2, camera: Camera): CanonicalBoneId | null;
  dispose(): void;
}

export function createSkeletonLayer(overlayGroup: Group, ctx: EditorContext): SkeletonLayer {
  const group = new Group();
  group.name = 'asx-skeleton';
  group.userData.editorOnly = true;
  overlayGroup.add(group);

  const jointGeometry = new SphereGeometry(JOINT_RADIUS, 10, 8);
  const pickGeometry = new SphereGeometry(PICK_RADIUS, 6, 5);
  const pickMaterial = new MeshBasicMaterial({ visible: false, depthTest: false });

  /** Display switch from the viewport bar; picking follows it, so a hidden skeleton is inert. */
  let shown = true;

  const joints = new Map<CanonicalBoneId, JointVisual>();
  const pickables: Mesh[] = [];
  const pickOwner = new Map<Object3D, CanonicalBoneId>();

  // --- canonical links ----------------------------------------------------

  const linkGeometry = new BufferGeometry();
  let linkPositions = new Float32Array(0);
  const links = new LineSegments(
    linkGeometry,
    new LineBasicMaterial({ color: COLOR_LINK, depthTest: false, transparent: true, opacity: 0.9 }),
  );
  links.renderOrder = 998;
  links.frustumCulled = false;
  links.userData.editorOnly = true;
  group.add(links);

  // --- faint source skeleton (MAP mode) -----------------------------------

  const sourceGeometry = new BufferGeometry();
  let sourcePositions = new Float32Array(0);
  const sourceLines = new LineSegments(
    sourceGeometry,
    new LineBasicMaterial({ color: COLOR_SOURCE, transparent: true, opacity: 0.45 }),
  );
  sourceLines.frustumCulled = false;
  sourceLines.visible = false;
  sourceLines.userData.editorOnly = true;
  group.add(sourceLines);

  /** `[childObject, parentObject]` pairs of the imported skeleton; rebuilt when the model does. */
  let sourcePairs: Array<[Object3D, Object3D]> = [];
  let sourceModelRoot: Object3D | null = null;

  function rebuildSourcePairs(): void {
    const root = ctx.session.model?.root ?? null;
    if (root === sourceModelRoot) return;
    sourceModelRoot = root;
    sourcePairs = [];
    if (!root) return;

    const objects = collectBoneObjects(root);
    for (const info of analyzeSkeleton(root).bones) {
      if (!info.parentName) continue;
      const child = objects.get(info.name);
      const parent = objects.get(info.parentName);
      if (child && parent) sourcePairs.push([child, parent]);
    }
    sourcePositions = new Float32Array(sourcePairs.length * 6);
    sourceGeometry.setAttribute('position', new BufferAttribute(sourcePositions, 3));
  }

  function updateSourceLines(visible: boolean): void {
    sourceLines.visible = visible && sourcePairs.length > 0;
    if (!sourceLines.visible) return;
    for (let i = 0; i < sourcePairs.length; i++) {
      const [child, parent] = sourcePairs[i];
      child.updateWorldMatrix(true, false);
      parent.updateWorldMatrix(true, false);
      const c = child.matrixWorld.elements;
      const p = parent.matrixWorld.elements;
      const o = i * 6;
      sourcePositions[o] = p[12];
      sourcePositions[o + 1] = p[13];
      sourcePositions[o + 2] = p[14];
      sourcePositions[o + 3] = c[12];
      sourcePositions[o + 4] = c[13];
      sourcePositions[o + 5] = c[14];
    }
    const attr = sourceGeometry.getAttribute('position');
    if (attr) attr.needsUpdate = true;
    sourceGeometry.computeBoundingSphere();
  }

  // --- joints -------------------------------------------------------------

  function ensureJoint(id: CanonicalBoneId): JointVisual {
    const existing = joints.get(id);
    if (existing) return existing;

    const material = new MeshBasicMaterial({ color: COLOR_NORMAL.clone(), depthTest: false });
    const dot = new Mesh(jointGeometry, material);
    dot.renderOrder = 999;
    dot.frustumCulled = false;
    dot.userData.editorOnly = true;

    const pick = new Mesh(pickGeometry, pickMaterial);
    pick.frustumCulled = false;
    pick.userData.editorOnly = true;
    pick.userData.boneId = id;

    group.add(dot, pick);
    pickables.push(pick);
    pickOwner.set(pick, id);

    const visual: JointVisual = { id, dot, pick, material };
    joints.set(id, visual);
    return visual;
  }

  function placeJoint(id: CanonicalBoneId, position: Vec3Tuple, selected: boolean, hovered: boolean): void {
    const visual = ensureJoint(id);
    visual.dot.position.set(position[0], position[1], position[2]);
    visual.pick.position.copy(visual.dot.position);
    visual.dot.visible = true;
    visual.pick.visible = true;
    // Selected reads as clearly distinct (bright yellow, bigger) rather than a shade of the
    // normal colour — "which bone am I editing" must be answerable at a glance.
    visual.material.color.copy(selected ? COLOR_SELECTED : hovered ? COLOR_HOVER : COLOR_NORMAL);
    const scale = selected ? 1.7 : hovered ? 1.3 : 1;
    visual.dot.scale.setScalar(scale);
  }

  function writeLinks(data: SkeletonOverlayData): void {
    const needed = data.links.length * 6;
    if (linkPositions.length !== needed) {
      linkPositions = new Float32Array(needed);
      linkGeometry.setAttribute('position', new BufferAttribute(linkPositions, 3));
    }
    for (let i = 0; i < data.links.length; i++) {
      const link = data.links[i];
      const o = i * 6;
      linkPositions[o] = link.from[0];
      linkPositions[o + 1] = link.from[1];
      linkPositions[o + 2] = link.from[2];
      linkPositions[o + 3] = link.to[0];
      linkPositions[o + 4] = link.to[1];
      linkPositions[o + 5] = link.to[2];
    }
    const attr = linkGeometry.getAttribute('position');
    if (attr) attr.needsUpdate = true;
    linkGeometry.computeBoundingSphere();
    links.visible = data.links.length > 0;
  }

  function update(): void {
    group.visible = shown;
    const state = ctx.store.getState();
    const mode = state.mode;

    rebuildSourcePairs();
    updateSourceLines(mode === 'map');

    const data = overlayForMode({
      mode,
      rig: ctx.session.rig,
      placement: state.rig.placement,
    });

    // In RIG mode the joint being placed is the selection that matters; elsewhere it is the
    // canonical bone selection shared with the hierarchy panel.
    const selected = mode === 'rig' ? state.rig.selectedJoint : state.selection.bone;
    const hovered = state.hover;

    for (const visual of joints.values()) {
      visual.dot.visible = false;
      visual.pick.visible = false;
    }
    for (const joint of data.joints) {
      placeJoint(joint.id, joint.position, joint.id === selected, joint.id === hovered);
    }
    writeLinks(data);
  }

  // --- picking ------------------------------------------------------------

  const raycaster = new Raycaster();

  function pick(ndc: Vector2, camera: Camera): CanonicalBoneId | null {
    // A hidden skeleton is not clickable: `group.visible` does not reach the raycaster.
    if (!shown) return null;
    const visible = pickables.filter((p) => p.visible);
    if (visible.length === 0) return null;
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObjects(visible, false);
    const first = hits[0];
    return first ? pickOwner.get(first.object) ?? null : null;
  }

  function dispose(): void {
    overlayGroup.remove(group);
    for (const visual of joints.values()) visual.material.dispose();
    joints.clear();
    pickables.length = 0;
    pickOwner.clear();
    jointGeometry.dispose();
    pickGeometry.dispose();
    pickMaterial.dispose();
    linkGeometry.dispose();
    links.material.dispose();
    sourceGeometry.dispose();
    sourceLines.material.dispose();
  }

  return {
    update,
    setVisible(visible) {
      shown = visible;
      group.visible = visible;
    },
    pick,
    dispose,
  };
}
